// ReactivateLeadAgentModal.hook.ts owns the data + state machine for the Lead
// Agent reactivation modal (A11 / RET-1343, Unit 3). It is deliberately NOT a
// flag on ConfirmTierModal's hook: reactivation charges immediately through its
// own A11 endpoints (docs/solutions/patterns/
// reactivation-is-not-change-plan-per-intent-semantics.md).
//
// Responsibilities:
//   - fetch the reactivate PREVIEW per selected tier (AbortController, fails
//     CLOSED so confirm stays disabled + retry on any error),
//   - perform the reactivate POST (single in-flight, idempotent prorationDate),
//   - drive a view machine over the A11 status branches, and
//   - run the 3DS step CLIENT-SIDE only (stripe.confirmCardPayment) — NEVER the
//     generic /tiers/confirm-3d-secure endpoint (it cancels the sub on a
//     declined 3DS; verified). Success relies on the Stripe webhook to finalize.
//
// Three race windows cross an await (preview, POST, confirmCardPayment); after
// each, bail if the active workspace changed (the modal reads workspaceId from
// the acceptance row, captured into a ref kept fresh each render).

import { useCallback, useEffect, useRef, useState } from 'react';
import type { AxiosError } from 'axios';
import { useStripe } from '@stripe/react-stripe-js';
import { LeadAgentAcceptanceService } from 'src/providers/LeadAgentAcceptanceProvider/LeadAgentAcceptance.service';
import type {
  tLeadAgentAcceptanceOrigin,
  tLeadAgentReactivatePreview,
  tLeadAgentReactivateResult,
  tLeadAgentSelectableTier,
} from 'src/providers/LeadAgentAcceptanceProvider/LeadAgentAcceptance.types';
import { extractPaymentIntentData } from 'src/pages/Settings/components/SubscriptionsManager/SubscriptionManager.functions';
import { SubscriptionManagerService } from 'src/pages/Settings/components/SubscriptionsManager/SubscriptionManager.service';
import type { tRenewalProjectionWireData } from 'src/pages/Settings/components/SubscriptionsManager/SubscriptionManager.types';
import type { tReactivateRefreshCode } from './ReactivateLeadAgentModal.types';
import { readLeadAgentErrorCode } from '../acceptanceModals.functions';

/**
 * View machine. `confirm` is the preview+tier-selection step; `processing` is
 * the in-flight POST / 3DS; `blocked` is a non-retryable business rule (4xx);
 * `error` is retryable (decline, transient 424, local/unknown).
 */
export type tReactivateView =
  | { status: 'confirm' }
  | { status: 'processing' }
  | { status: 'success'; result: tLeadAgentReactivateResult }
  | {
      status: 'error';
      /** Inline message (decline reason / generic), surfaced in the modal. */
      message: string | null;
    }
  | {
      status: 'blocked';
      /** Machine code only. API-authored copy is never rendered. */
      code: string | null;
    };

export type tReactivatePreviewState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; data: tLeadAgentReactivatePreview };

/**
 * Next-renewal projection (A6 / RET-1287) shown as an INFORMATIONAL layer
 * (RET-1417). Unlike ConfirmTierModal — which is fail-CLOSED on this endpoint
 * because the projection IS the amount it confirms — reactivation charges from
 * the A11 preview, so the projection here is fail-OPEN: a `loading`/`error`
 * state hides the renewal section but NEVER gates the charge, checkbox, or
 * submit (that would re-create the documented fail-closed trap:
 * feedback_la_accept_failcloses_on_projection_endpoint).
 */
export type tReactivateProjectionState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; data: tRenewalProjectionWireData };

export type tUseReactivateLeadAgentModalArgs = {
  workspaceId: string;
  /** Suggested tier from the acceptance row (default GROWTH upstream). */
  suggestedTier: tLeadAgentSelectableTier;
  /**
   * Personal first-charge date from the acceptance row; forwarded to the
   * renewal projection (omitted from the query when null). Informational only.
   */
  nextPaymentAt: string | null;
  /**
   * Acceptance cohort (RET-1290), forwarded to the projection so the breakdown
   * reflects the bridge-vs-full first charge. Omitted from the query when null.
   */
  origin: tLeadAgentAcceptanceOrigin | null;
};

export type tUseReactivateLeadAgentModal = {
  view: tReactivateView;
  preview: tReactivatePreviewState;
  /** Informational next-renewal projection; never gates the charge (fail-open). */
  projection: tReactivateProjectionState;
  selectedTier: tLeadAgentSelectableTier;
  /** Confirm is enabled only when a preview is ready and we are not in flight. */
  confirmDisabled: boolean;
  selectTier: (tier: tLeadAgentSelectableTier) => void;
  /** Performs the reactivate POST (+ 3DS when required). */
  confirm: () => void;
  /** Refreshes terminal failures; ambiguous failures reuse the pinned preview. */
  retry: () => void;
  /** Localized by the component after an automatic conflict refresh. */
  noticeCode: tReactivateRefreshCode | null;
};

function isCanceled(error: unknown): boolean {
  return (error as AxiosError | undefined)?.code === 'ERR_CANCELED';
}

function isRefreshCode(code: string | null): code is tReactivateRefreshCode {
  return (
    code === 'stale_reactivation_preview' || code === 'renewal_in_progress'
  );
}

function statusOf(error: unknown): number | undefined {
  return (error as AxiosError | undefined)?.response?.status;
}

/**
 * Maps a thrown reactivate error to a terminal view. 4xx/422 are business-rule
 * BLOCKS (not retryable); 424 is a transient comms fault (retryable error);
 * everything else (5xx, local/unknown) is a defensive retryable error.
 */
function viewForThrownError(error: unknown): tReactivateView {
  const status = statusOf(error);
  if (status === 424) {
    return { status: 'error', message: null };
  }
  if (status !== undefined && status >= 400 && status < 500) {
    return { status: 'blocked', code: readLeadAgentErrorCode(error) };
  }
  return { status: 'error', message: null };
}

export function useReactivateLeadAgentModal(
  args: tUseReactivateLeadAgentModalArgs,
): tUseReactivateLeadAgentModal {
  const { workspaceId, suggestedTier, nextPaymentAt, origin } = args;
  const stripe = useStripe();

  const [selectedTier, setSelectedTier] =
    useState<tLeadAgentSelectableTier>(suggestedTier);
  const [preview, setPreview] = useState<tReactivatePreviewState>({
    status: 'idle',
  });
  // Informational renewal projection (RET-1417). Independent of `preview`; its
  // state never feeds `confirmDisabled` (fail-open).
  const [projection, setProjection] = useState<tReactivateProjectionState>({
    status: 'idle',
  });
  const [view, setView] = useState<tReactivateView>({ status: 'confirm' });
  const [noticeCode, setNoticeCode] = useState<tReactivateRefreshCode | null>(
    null,
  );
  // Bumped on preview retry to re-run the preview effect without changing tier.
  const [previewNonce, setPreviewNonce] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  // Separate controller for the projection fetch so a tier switch aborts the
  // stale projection independently of the preview.
  const projectionAbortRef = useRef<AbortController | null>(null);
  // Unmount guard: the confirm flow crosses up to three awaits (POST + 3DS),
  // each followed by a setView. If the modal unmounts mid-flight (route nav,
  // parent close), those setStates would warn / no-op — bail on each instead.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  // Synchronous in-flight latch — `view==='processing'` is async React state, so
  // two same-tick clicks would both read the pre-update value and fire two
  // POSTs. This ref flips synchronously so the 2nd click bails.
  const inFlightRef = useRef(false);
  // Ambiguous failures must replay the same backend idempotency key. Only a
  // confirmed terminal outcome is allowed to mint a new pinned preview date.
  const refreshPreviewOnRetryRef = useRef(false);
  // Gate the suggested-tier auto-init so a re-render can't clobber a user pick
  // (feedback_useref_to_block_autoinit_after_user_picked). The initial state
  // already seeds suggestedTier; this ref only guards future suggestion drift.
  const hasUserPickedRef = useRef(false);
  // Active workspace captured fresh each render — read after every await to bail
  // if the user switched workspaces mid-flight (3 race windows).
  const activeWorkspaceIdRef = useRef(workspaceId);
  activeWorkspaceIdRef.current = workspaceId;

  // Re-sync the selected tier to a NEW suggestion (e.g. the acceptance row
  // refetched in the background) only until the Owner has picked one — after
  // that the pick wins (feedback_useref_to_block_autoinit_after_user_picked).
  useEffect(() => {
    // Also bail while a charge is in flight: resyncing the tier mid-`processing`
    // would re-arm the preview (tier is a preview dep) and thrash the amount the
    // user is about to be charged.
    if (hasUserPickedRef.current || view.status === 'processing') return;
    setSelectedTier(suggestedTier);
  }, [suggestedTier, view.status]);

  // ---- Preview fetch (per tier / retry), fails CLOSED ----
  useEffect(() => {
    if (!workspaceId) {
      setPreview({ status: 'error' });
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const requestWorkspaceId = workspaceId;

    setPreview((current) =>
      current.status === 'loading' ? current : { status: 'loading' },
    );

    LeadAgentAcceptanceService.getReactivatePreview(workspaceId, {
      tier: selectedTier,
      signal: controller.signal,
    })
      .then((data) => {
        if (controller.signal.aborted) return;
        // Workspace mid-await guard (race window 1): a switch during the fetch
        // must not apply a stale preview.
        if (activeWorkspaceIdRef.current !== requestWorkspaceId) return;
        setPreview({ status: 'ready', data });
      })
      .catch((error: unknown) => {
        if (isCanceled(error) || controller.signal.aborted) return;
        if (activeWorkspaceIdRef.current !== requestWorkspaceId) return;
        setPreview({ status: 'error' });
      });

    return () => {
      controller.abort();
    };
  }, [workspaceId, selectedTier, previewNonce]);

  // ---- Renewal projection fetch (per tier), fails OPEN ----
  // Informational only: a failure sets `projection: 'error'` and otherwise
  // no-ops — it must NEVER touch `preview`/`view`/`confirmDisabled`, or the
  // charge would be blocked by a non-essential read (the fail-closed trap).
  useEffect(() => {
    if (!workspaceId) {
      setProjection({ status: 'error' });
      return;
    }

    projectionAbortRef.current?.abort();
    const controller = new AbortController();
    projectionAbortRef.current = controller;
    const requestWorkspaceId = workspaceId;

    setProjection((current) =>
      current.status === 'loading' ? current : { status: 'loading' },
    );

    SubscriptionManagerService.getRenewalProjection(
      workspaceId,
      { tier: selectedTier, nextPaymentAt, origin: origin ?? undefined },
      controller.signal,
    )
      .then((data) => {
        if (controller.signal.aborted) return;
        // Workspace mid-await guard: a switch during the fetch must not apply a
        // stale projection.
        if (activeWorkspaceIdRef.current !== requestWorkspaceId) return;
        setProjection({ status: 'ready', data });
      })
      .catch((error: unknown) => {
        // Aborted on tier switch — keep the newer request's loading state.
        if (isCanceled(error) || controller.signal.aborted) return;
        if (activeWorkspaceIdRef.current !== requestWorkspaceId) return;
        // Fail OPEN: hide the section, do not disturb the charge path.
        setProjection({ status: 'error' });
      });

    return () => {
      controller.abort();
    };
  }, [workspaceId, selectedTier, nextPaymentAt, origin]);

  const selectTier = useCallback((tier: tLeadAgentSelectableTier) => {
    hasUserPickedRef.current = true;
    setNoticeCode(null);
    setSelectedTier(tier);
    // Switching tier returns to the confirm step (a prior decline/error for the
    // old tier no longer applies) and re-arms the preview via the tier dep.
    setView({ status: 'confirm' });
  }, []);

  const refreshPreview = useCallback(
    (notice: tReactivateRefreshCode | null = null) => {
      setNoticeCode(notice);
      setPreview({ status: 'loading' });
      setPreviewNonce((n) => n + 1);
      setView({ status: 'confirm' });
    },
    [],
  );

  const runConfirm = useCallback(async () => {
    // Synchronous double-submit guard.
    if (inFlightRef.current) return;
    if (preview.status !== 'ready') return;

    const requestWorkspaceId = activeWorkspaceIdRef.current;
    const tierToConfirm = selectedTier;
    // The previewed prorationDate MUST be echoed verbatim so the charge is
    // idempotent on retry (sub+price+prorationDate) and matches the shown amount.
    const prorationDate = preview.data.prorationDate;

    inFlightRef.current = true;
    setNoticeCode(null);
    setView({ status: 'processing' });

    try {
      const result = await LeadAgentAcceptanceService.reactivateLeadAgent(
        requestWorkspaceId,
        { tier: tierToConfirm, prorationDate },
      );
      // Unmount guard (race window 2): the modal is gone — nothing to update.
      if (!isMountedRef.current) {
        inFlightRef.current = false;
        return;
      }
      // Workspace mid-await guard (race window 2).
      if (activeWorkspaceIdRef.current !== requestWorkspaceId) {
        inFlightRef.current = false;
        return;
      }

      // 'ok' = charge_automatically paid/processing. 'pending' = send_invoice
      // invoice emailed, awaiting manual payment (RET-1649). Both are terminal,
      // dismissible outcomes routed to the success view; the copy differentiates
      // them. For 'pending' nothing is provisioned yet — the paid-invoice webhook
      // (RET-1539) flips the entitlement when the customer pays.
      if (result.status === 'ok' || result.status === 'pending') {
        setView({ status: 'success', result });
        return;
      }

      if (result.status === 'failed') {
        // 200 + failed = payment decline (retryable). Surface the message.
        refreshPreviewOnRetryRef.current = true;
        setView({ status: 'error', message: result.message });
        return;
      }

      // requires_action: confirm the 3DS challenge CLIENT-SIDE. NEVER call a
      // backend confirm endpoint here (the generic /tiers/confirm-3d-secure
      // cancels the subscription on a declined 3DS — verified, forbidden).
      const clientSecret = result.paymentIntentClientSecret;
      if (!stripe || !clientSecret) {
        refreshPreviewOnRetryRef.current = false;
        setView({ status: 'error', message: null });
        return;
      }

      const confirmation = await stripe.confirmCardPayment(clientSecret);
      // Unmount guard (race window 3).
      if (!isMountedRef.current) {
        inFlightRef.current = false;
        return;
      }
      // Workspace mid-await guard (race window 3).
      if (activeWorkspaceIdRef.current !== requestWorkspaceId) {
        inFlightRef.current = false;
        return;
      }

      const { status } = extractPaymentIntentData(confirmation);
      if (confirmation.error || status !== 'succeeded') {
        // Declined / abandoned 3DS — retryable. Surface the decline reason when
        // Stripe provides one (mirrors the 200+failed branch); rely on the
        // webhook for the succeeded case; never confirm against the backend.
        refreshPreviewOnRetryRef.current = true;
        setView({
          status: 'error',
          message: confirmation.error?.message ?? null,
        });
        return;
      }

      // 3DS succeeded: charge processing, webhook finalizes. Reuse the A11
      // result (it already carries the tier/invoiceId) for the success copy.
      setView({ status: 'success', result });
    } catch (error) {
      if (!isMountedRef.current) return;
      if (activeWorkspaceIdRef.current !== requestWorkspaceId) return;
      const code = readLeadAgentErrorCode(error);
      if (isRefreshCode(code)) {
        refreshPreview(code);
        return;
      }
      const thrownView = viewForThrownError(error);
      if (thrownView.status === 'error') {
        // The POST or Stripe confirmation may still complete after a timeout,
        // 424/5xx, network failure, or rejected promise. Keep the exact pinned
        // date so the next POST reuses the original backend idempotency key.
        refreshPreviewOnRetryRef.current = false;
      }
      setView(thrownView);
    } finally {
      inFlightRef.current = false;
    }
  }, [preview, refreshPreview, selectedTier, stripe]);

  const confirm = useCallback(() => {
    void runConfirm();
  }, [runConfirm]);

  const retry = useCallback(() => {
    if (inFlightRef.current) return;
    if (preview.status !== 'ready' || refreshPreviewOnRetryRef.current) {
      refreshPreviewOnRetryRef.current = false;
      refreshPreview();
      return;
    }
    setNoticeCode(null);
    setView({ status: 'confirm' });
  }, [preview.status, refreshPreview]);

  const confirmDisabled =
    preview.status !== 'ready' || view.status === 'processing';

  return {
    view,
    preview,
    projection,
    selectedTier,
    confirmDisabled,
    selectTier,
    confirm,
    retry,
    noticeCode,
  };
}
